import _ from "lodash";
import React, { useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useQuery, useQueryCache } from "react-query";
import { StreamTable, useStreamQuery } from "../../StreamTable";
import { useOmniFilter } from "../../OmniFilter";
import { useCrmFilters } from "../../filters/CrmFilters";
import { useCrmAggregations } from "../../filters/AggregationFilter";
import { IElasticsearchSort } from "../../../utils/elasticsearch";
import JsonApi from "../../../utils/json_api";
import networkSelectors from "../../../selectors/network";
import { useClient } from "../../../hooks/use-client";
import { useStreamResource } from "../../../hooks/use-stream-resource";
import { useEmployees } from "../../../queries/employees";
import { fetchGetDepartments } from "../../../queries/departments";
import { fetchGetCrmTopics } from "../../../queries/crmTopics";
import { fetchSearchCrmRequests } from "../../../queries/search";
import { getCrmStatuses } from "../../../queries/crmStatuses";

const defaultSort = {
  field: "dateEntered",
  direction: "desc",
} as IElasticsearchSort;

export const CrmStreamTable = (props: any) => <StreamTable {...props} />;

export const useCrmRequests = (key: string, site: string) => {
  const { clientId } = useClient(site);
  const dispatch = useDispatch();
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const queryCache = useQueryCache();
  const searchFn = fetchSearchCrmRequests(networkHeaders);

  const EmployeesQuery = useEmployees({ includeDeactivated: true, fields: ["first-name", "last-name"] });
  const departmentsQuery = useQuery(["departments"], fetchGetDepartments(dispatch));
  const topicsQuery = useQuery(["crm.topics"], fetchGetCrmTopics(dispatch));
  const statusesQuery = useQuery(["crm.statuses"], getCrmStatuses(networkHeaders));

  const topicsData = useMemo(() => _.get(topicsQuery.data, "payload.data", []), [topicsQuery.data]);
  const departmentsData = useMemo(() => _.get(departmentsQuery.data, "payload.data", []), [departmentsQuery.data]);

  const aggregations = useCrmAggregations(clientId);
  const omnifilters = useOmniFilter(
    useCrmFilters(queryCache, EmployeesQuery.query, topicsQuery, departmentsQuery, statusesQuery, aggregations),
  );

  const streamQueryFn = (
    key: string,
    size: number,
    refreshCursor: any,
    setRefreshCursor: any,
    filter: any,
    reverseSort: any,
    sort: any,
    enabled: any,
    hits: any,
    setHits: any,
  ) =>
    useStreamQuery({
      key: key,
      size: size,
      refreshCursor,
      setRefreshCursor,
      filter,
      reverseSort: reverseSort,
      sort: sort,
      enabled: enabled,
      queryFn: searchFn,
      setHits,
    });

  const rowBuilder = useCallback(
    (hits: any) => {
      return _.flatten(hits).map((item: any) => {
        const contactDisplayName = _.join([item._source.contact?.firstName, item._source.contact?.lastName], " ");
        const contactIsEmployee = !!JsonApi.findById(EmployeesQuery.data, item._source["contactId"].toString());
        const employeeData = JsonApi.findById(EmployeesQuery.data, item._source["assignedToId"].toString());
        const assignedToDisplayName = employeeData
          ? `${_.get(employeeData, "attributes.first-name", "")} ${_.get(employeeData, "attributes.last-name", "")}`
          : "Unassigned";
        const topicData = JsonApi.findById(topicsData, item._source["classificationId"].toString());
        const topicName = _.get(topicData, "attributes.name");
        const departmentData = JsonApi.findById(departmentsData, item._source["departmentId"].toString());
        const departmentName = _.get(departmentData, "attributes.name");
        return Object.assign({}, item._source, {
          editLink: `/${site}/crm/${item._source["caseId"]}`,
          topicName,
          departmentName,
          contactDisplayName,
          contactIsEmployee,
          assignedToDisplayName,
        });
      });
    },
    [EmployeesQuery.data, topicsData, departmentsData],
  );

  return useStreamResource(key, "crm", omnifilters, streamQueryFn, rowBuilder, defaultSort, []);
};
